import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { FileText, Plus, Trash2, Edit2, X, Save, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DocumentTypes() {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', aliases: '', category: 'credential' });
  const [saving, setSaving] = useState(false);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/document-types?active_only=false`);
      setTypes(res.data || []);
    } catch (e) {
      toast.error('Failed to load document types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTypes(); }, []);

  const resetForm = () => {
    setForm({ name: '', description: '', aliases: '', category: 'credential' });
    setEditingId(null);
    setShowAddForm(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const aliases = form.aliases.split(',').map(a => a.trim()).filter(Boolean);
      const payload = { name: form.name.trim(), description: form.description.trim() || null, aliases, category: form.category };
      if (editingId) {
        await axios.put(`${API}/document-types/${editingId}`, payload);
        toast.success('Document type updated');
      } else {
        await axios.post(`${API}/document-types`, payload);
        toast.success('Document type created');
      }
      resetForm();
      fetchTypes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, description: t.description || '', aliases: (t.aliases || []).join(', '), category: t.category || 'credential' });
    setShowAddForm(true);
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/document-types/${t.id}`);
      toast.success('Document type deleted');
      fetchTypes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggleActive = async (t) => {
    try {
      await axios.put(`${API}/document-types/${t.id}`, { is_active: !t.is_active });
      fetchTypes();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-to-admin-btn">
          <ArrowLeft size={16} className="mr-1" /> Back to Admin
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-2">
            <FileText className="text-primary" /> Document Types
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage credential and document categories. The AI uses this list to classify uploaded credentials.
            Anything it can't recognize is filed as <span className="font-semibold">"Other Document"</span>.
          </p>
        </div>
        <Button onClick={() => { resetForm(); setShowAddForm(true); }} data-testid="add-document-type-btn">
          <Plus size={16} className="mr-1" /> Add Type
        </Button>
      </div>

      {showAddForm && (
        <Card className="p-6" data-testid="document-type-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">{editingId ? 'Edit Document Type' : 'Add Document Type'}</h3>
            <Button variant="ghost" size="sm" onClick={resetForm}><X size={16} /></Button>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Name <span className="text-red-500">*</span></Label>
              <Input
                placeholder="e.g. Hep B Vaccination"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="document-type-name-input"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                placeholder="Short description shown to admins"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="document-type-description-input"
              />
            </div>
            <div>
              <Label>Aliases (comma-separated, optional)</Label>
              <Input
                placeholder="e.g. hep b, hepatitis b vaccine, hbv"
                value={form.aliases}
                onChange={(e) => setForm({ ...form, aliases: e.target.value })}
                data-testid="document-type-aliases-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Helps the AI recognize variations of the same document.
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving} data-testid="save-document-type-btn">
                {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
                {editingId ? 'Update' : 'Create'}
              </Button>
              <Button variant="ghost" onClick={resetForm}>Cancel</Button>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-muted-foreground" size={32} />
          </div>
        ) : types.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No document types yet. Click "Add Type" to create one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="document-types-table">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground uppercase">
                  <th className="pb-2 pl-2">Name</th>
                  <th className="pb-2">Description</th>
                  <th className="pb-2">Aliases</th>
                  <th className="pb-2">Category</th>
                  <th className="pb-2">Default</th>
                  <th className="pb-2">Active</th>
                  <th className="pb-2 text-right pr-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {types.map(t => (
                  <tr key={t.id} className="border-b hover:bg-muted/30" data-testid={`document-type-row-${t.id}`}>
                    <td className="py-3 pl-2 font-medium">{t.name}</td>
                    <td className="py-3 text-muted-foreground">{t.description || '—'}</td>
                    <td className="py-3 text-xs text-muted-foreground">
                      {(t.aliases || []).slice(0, 3).join(', ')}
                      {(t.aliases || []).length > 3 && ` +${t.aliases.length - 3}`}
                    </td>
                    <td className="py-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        t.category === 'tax' ? 'bg-purple-100 text-purple-700' :
                        t.category === 'other' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {t.category || 'credential'}
                      </span>
                    </td>
                    <td className="py-3">
                      {t.is_default && (
                        <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">Default</span>
                      )}
                    </td>
                    <td className="py-3">
                      <Switch
                        checked={t.is_active}
                        onCheckedChange={() => handleToggleActive(t)}
                        data-testid={`toggle-active-${t.id}`}
                      />
                    </td>
                    <td className="py-3 pr-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(t)} data-testid={`edit-${t.id}`}>
                        <Edit2 size={14} />
                      </Button>
                      {!t.is_default && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(t)} data-testid={`delete-${t.id}`}>
                          <Trash2 size={14} className="text-red-500" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
